import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { calcBreakEven, formatRoas } from "@/lib/signal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Store, ArrowLeft } from "lucide-react";

export default function OnboardingBrand() {
  const [storeName, setStoreName] = useState("");
  const [sp, setSp] = useState("");
  const [cogs, setCogs] = useState("");
  const [other, setOther] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";

  // Returning-user guard: if brand already exists AND at least one log,
  // this is a returning user — send them straight to the dashboard.
  // BUT: skip the guard when the user is explicitly editing their brand.
  useEffect(() => {
    if (!user) return;
    if (!editMode && user.has_brand && user.log_count > 0) {
      navigate("/dashboard", { replace: true });
      return;
    }
    api.get("/brand").then((r) => {
      if (r.data) {
        setStoreName(r.data.store_name || "");
        setSp(String(r.data.selling_price ?? ""));
        setCogs(String(r.data.cogs ?? ""));
        setOther(String(r.data.other_costs ?? ""));
      }
    }).catch(() => {});
  }, [user, navigate, editMode]);

  const be = useMemo(
    () => calcBreakEven(Number(sp) || 0, Number(cogs) || 0, Number(other) || 0),
    [sp, cogs, other]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!storeName.trim() || !sp || !cogs) {
      toast.error("Store name, selling price and COGS are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/brand", {
        store_name: storeName.trim(),
        selling_price: Number(sp),
        cogs: Number(cogs),
        other_costs: Number(other) || 0,
      });
      if (editMode) {
        toast.success("Brand updated");
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/onboarding/first-log", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save brand");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-12">
        {editMode ? (
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="edit-brand-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        ) : (
          <Stepper step={1} />
        )}
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-6">
          {editMode ? "Edit your brand" : "Tell us about your brand"}
        </h1>
        <p className="mt-2 text-slate-600">
          {editMode
            ? "Update your product economics. Your break-even ROAS will recalculate on your next weekly log."
            : "We use these numbers to compute your real break-even ROAS."}
        </p>

        <Card className="mt-8 p-6 sm:p-8 bg-white rounded-2xl border-slate-200">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="store" className="text-xs font-bold uppercase tracking-widest text-slate-500">Store name</Label>
              <div className="relative mt-2">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="store" required value={storeName} onChange={(e) => setStoreName(e.target.value)} className="pl-10 h-11" placeholder="Acme Threads" data-testid="brand-store-input" />
              </div>
            </div>
            <Money id="brand-sp" label="Selling price per unit" value={sp} onChange={setSp} testid="brand-selling-price-input" />
            <Money id="brand-cogs" label="COGS per unit" value={cogs} onChange={setCogs} testid="brand-cogs-input" />
            <Money id="brand-other" label="Other costs per order (shipping, fees, packaging)" value={other} onChange={setOther} testid="brand-other-costs-input" />

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Your break-even ROAS</p>
              <p className="font-display font-bold text-4xl text-slate-900 mt-1" data-testid="brand-break-even-display">{formatRoas(be)}</p>
              <p className="text-sm text-slate-500 mt-1">Every ad dollar must return at least ${formatRoas(be)} to be profitable.</p>
            </div>

            <Button type="submit" disabled={saving} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="brand-submit-button">
              {saving ? "Saving…" : editMode ? (<>Save changes <ArrowRight className="ml-2 h-4 w-4" /></>) : (<>Continue <ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}

const Money = ({ id, label, value, onChange, testid }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</Label>
    <div className="relative mt-2">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
      <Input
        id={id}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-7 h-11"
        data-testid={testid}
      />
    </div>
  </div>
);

const Stepper = ({ step }) => (
  <div className="flex items-center gap-3">
    {[1, 2].map((n) => (
      <div key={n} className="flex items-center gap-3">
        <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${n <= step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
          {n}
        </span>
        {n === 1 && <div className="h-px w-10 bg-slate-200" />}
      </div>
    ))}
    <span className="ml-2 text-xs uppercase tracking-widest text-slate-400 font-bold">Step {step} of 2</span>
  </div>
);
